


























































































































































































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import MobileDetect from 'mobile-detect';
import { FormState } from '@/components/prine/enums/FormState';
import ConfirmModal from '@/components/prine/ConfirmModal.vue';

import type { Project } from '@/models/project';
import type { Trade } from '@/models/trade';
import type { User } from '@/models/user';
import type { Lesson } from '@/models/lesson';
import type { Topic } from '@/models/topic';
import type { Phase } from '@/models/phase';
import type { Tag } from '@/models/tag';

import { tradeWebservice } from '@/webservices/trade';
import { phaseWebservice } from '@/webservices/phase';
import { lessonWebservice } from '@/webservices/lesson';
import { topicWebservice } from '@/webservices/topic';
import { tagWebservice } from '@/webservices/tag';
import type { CompanySetting } from '@/models/companySetting';
import WysiwygEditor from '@/components/prine/WysiwygEditor.vue';
import { projectWebservice } from '@/webservices/project';
import PrineButton from '@/components/prine/PrineButton.vue';
import PreviewLessons from '@/components/knowledge/lessons/PreviewLessons.vue';
import { hasPermission } from '@/helpers/auth/permissionHelpers';
import { resolveLessonTypeByLesson } from '@/helpers/resolveLessonType';
import { PERMISSIONS } from '@/helpers/auth/permissions';

@Component({
    name: 'StoreUpdateLessonModal',
    computed: {
        PERMISSIONS() {
            return PERMISSIONS;
        },
    },
    methods: { resolveLessonType: resolveLessonTypeByLesson, hasPermission },
    components: {
        WysiwygEditor,
        ConfirmModal,
        PrineButton,
        PreviewLessons,
    },
})
export default class StoreUpdateLessonModal extends Vue {
    public FormState = FormState;

    // Data
    private disabled: boolean = false;
    private errorMsg: string = '';
    private isConfirmModalActive: boolean = false;
    private showMandatoryOption: boolean = false;

    private state: FormState = FormState.New;

    private projectOnly: boolean = false;

    private topic: Topic | null = null;
    private lesson: Lesson = {} as Lesson;
    private lessons = new Array<Lesson>();
    private checkedParentLesson: Lesson[] = new Array<Lesson>();
    private parentLesson: Lesson | null = null;
    private trades = new Array<Trade>();

    private loading = {
        data: false,
        color: '#fdc05d',
        size: '40px',
    };

    private phases = new Array<Phase>();
    private tags: Tag[] = new Array<Tag>();
    private topics = new Array<Topic>();
    private filteredPhases = new Array<Phase>();
    private selectedPhases = new Array<Phase>();
    private selectedTradeDropdown: Trade = {} as Trade;
    private selectedProjectDropdown: Project = {} as Project;
    private selectedTags: Tag[] = new Array<Tag>();
    private projects: Project[] = new Array<Project>();

    @Getter('account/userInfo') private userInfo: User;
    @Getter('project/selectedProject') private selectedProject: Project;
    @Getter('account/companySetting') private companySetting: CompanySetting;

    @Prop() private selectedTrade: Trade;
    @Prop() private selectedLesson: Lesson;

    @Watch('topic')
    private onTopicChanged(newTopic, oldTopic) {
        if (oldTopic === newTopic || !newTopic) {
            return;
        }

        this.loadLessons(newTopic.id);
        this.parentLesson = null;
    }

    @Watch('selectedTradeDropdown')
    private onTradeChanged(newTrade, oldTrade) {
        if (oldTrade === newTrade || !this.topic) {
            return;
        }

        this.loadLessons(this.topic.id);
        this.parentLesson = null;
    }

    @Watch('parentLesson')
    private onParentLessonChanged(value) {
        if (value) {
            this.selectedPhases = value.phases;
        } else {
            this.selectedPhases = [];
        }
    }

    // Mounted is called initially
    private mounted() {
        this.loading.data = true;
        const promises = [this.loadTopics(), this.loadPhases(), this.loadTags(), this.loadProjects()];

        // If the user is content admin or master show mandatory option
        this.showMandatoryOption = hasPermission(PERMISSIONS.APPEX_LESSON_MANDATORY);

        // If a lesson is passed, we need to load the lesson and set the state to update
        if (this.selectedLesson && this.selectedLesson.id) {
            promises.push(this.loadTrades());
            promises.push(this.loadLesson(this.selectedLesson.id));
            this.state = FormState.Update;
        } else {
            this.selectedTradeDropdown = this.selectedTrade;
        }

        Promise.all(promises)
            .then(() => {
                this.loading.data = false;
            })
            .catch((e) => {
                console.error(e);
                this.loading.data = false;
            });
    }

    private selectedParentLesson(value: Lesson[]) {
        this.checkedParentLesson = value;
        if (value.length !== 0) {
            this.parentLesson = value[0];
        } else {
            this.parentLesson = null;
        }
    }

    private get isDesktop() {
        const isNotDesktop = window.matchMedia('only screen and (max-width: 1300px)').matches;
        const md = new MobileDetect(window.navigator.userAgent);
        return !(md.mobile() || md.phone() || md.tablet() || isNotDesktop);
    }

    private async loadTopics() {
        try {
            const response = await topicWebservice.load();
            const filteredTopics = response.data.filter((topic: Topic) => {
                return topic.title !== 'Nicht zugeordnete Lehren';
            });

            this.topics = filteredTopics.sort((a: Topic, b: Topic) => {
                return a.title.localeCompare(b.title);
            });
        } catch (e) {
            console.error(e);
        }
    }

    private async loadPhases() {
        try {
            const response = await phaseWebservice.load();
            this.phases = response.data;
        } catch (e) {
            console.error(e);
        }
    }

    private async loadTags() {
        try {
            const response = await tagWebservice.load();
            this.tags = response.data;

            if (!this.selectedLesson) {
                this.selectedTags = this.tags.filter((tag) => tag.default);
            }
        } catch (e) {
            console.error(e);
        }
    }

    private async loadProjects() {
        try {
            const response = await projectWebservice.loadAll();
            this.projects = response.data;
        } catch (e) {
            console.error(e);
        }
    }

    private async loadTrades() {
        try {
            const response = await tradeWebservice.load(false);
            this.trades = response.data;
        } catch (e) {
            console.error(e);
        }
    }

    private async loadLesson(lessonId) {
        try {
            const response = await lessonWebservice.getById(lessonId);
            this.lesson = response.data;
            this.selectedPhases = response.data.phases;
            this.selectedTags = response.data.tags;

            // Pre initialize the trades as an empty array
            this.lesson.trades = [];

            // If the selected trade is passed, set the value for the dropdown
            if (this.selectedTrade && this.selectedTrade.id) {
                this.selectedTradeDropdown = this.selectedTrade;
            }

            if (this.lesson.project) {
                this.selectedProjectDropdown = this.lesson.project;
            }

            if (this.lesson.topic) {
                this.topic = this.lesson.topic;
            }
        } catch (e) {
            console.error(e);
        }
    }

    private async loadLessons(topicId) {
        try {
            const response = await lessonWebservice.load(this.selectedTradeDropdown.id, topicId);
            this.lessons = response.data;

            if (this.lesson.parent) {
                this.parentLesson = this.lesson.parent;
                this.checkedParentLesson = [];
                this.checkedParentLesson.push(this.parentLesson);
            }
        } catch (e) {
            console.error(e);
        }
    }

    private save() {
        this.disabled = true;
        if (!this.validate()) {
            this.disabled = false;
            return 0;
        }
        if (this.topic !== null) {
            this.lesson.trades = [this.selectedTradeDropdown];
            if (this.state === FormState.New) {
                lessonWebservice
                    .store(
                        this.lesson,
                        this.selectedTrade.id,
                        this.topic.id,
                        this.selectedPhases,
                        this.selectedTags,
                        null,
                        this.selectedProjectDropdown ? this.selectedProjectDropdown.id : 0,
                        this.parentLesson
                    )
                    .then((response) => {
                        this.$buefy.toast.open({
                            duration: 3000,
                            message: this.$t('toastMessages.store.lesson').toString(),
                            type: 'is-success',
                        });
                        this.$emit('toggleStoreUpdateLessonModal');
                        this.$emit('syncKnowledge');
                    })
                    .catch((error) => {
                        this.disabled = false;
                        console.log(error);
                    });
            } else if (this.state === FormState.Update) {
                // tslint:disable-next-line:max-line-length
                lessonWebservice
                    .update(
                        this.lesson,
                        this.selectedPhases,
                        this.topic,
                        this.selectedTags,
                        this.selectedProjectDropdown ? this.selectedProjectDropdown.id : 0,
                        this.parentLesson
                    )
                    .then((response) => {
                        this.$buefy.toast.open({
                            duration: 3000,
                            message: this.$t('toastMessages.update.lesson').toString(),
                            type: 'is-success',
                        });
                        this.$emit('toggleStoreUpdateLessonModal');
                        this.$emit('syncKnowledge');
                        this.disabled = false;
                    })
                    .catch((error) => {
                        this.disabled = false;
                        console.log(error);
                    });
            }
        }
    }

    private toggleConfirmModal(answer) {
        this.isConfirmModalActive = !this.isConfirmModalActive;
        if (answer === true) {
            this.archive();
        }
    }

    private archive() {
        lessonWebservice
            .remove(this.lesson)
            .then((response) => {
                this.$buefy.toast.open({
                    duration: 3000,
                    message: this.$t('toastMessages.delete.lesson').toString(),
                    type: 'is-success',
                });
                this.$emit('toggleStoreUpdateLessonModal');
                this.$emit('syncKnowledge');
            })
            .catch(function (error) {
                console.log(error);
            });
    }

    private validate() {
        if (!this.lesson.insight || this.lesson.insight === '') {
            this.errorMsg = 'insight';
            return 0;
        } else if (this.topic === null || !this.topic.title || this.topic.title === '') {
            this.errorMsg = 'topic';
            return 0;
        } else if (!this.selectedPhases || this.selectedPhases.length === 0) {
            this.errorMsg = 'phase';
            return 0;
        } else {
            return true;
        }
    }

    private bkpTrade({ bkp, title }) {
        return `${bkp} ${title}`;
    }
}
